import { guideLine } from '../../shared/guide-link.js';

export const LAND_USE_CATEGORIES = {
    agricultural: { label: 'Grunty rolne', color: '#e6c65c' },
    forest: { label: 'Grunty leśne oraz zadrzewione', color: '#5a9f68' },
    builtUp: { label: 'Grunty zabudowane i zurbanizowane', color: '#d77973' },
    transport: { label: 'Tereny komunikacyjne', color: '#89939f' },
    water: { label: 'Grunty pod wodami', color: '#579fc8' },
    other: { label: 'Pozostałe użytki', color: '#aa8d73' },
    mixed: { label: 'Użytki mieszane', color: '#8d78b8' },
    noData: { label: 'Brak danych', color: '#d7d7d7' },
};

const LAND_USE_CODES = {
    R: { label: 'Grunty orne', category: 'Grunty rolne' },
    S: { label: 'Sady', category: 'Grunty rolne' },
    'Ł': { label: 'Łąki trwałe', category: 'Grunty rolne' },
    Ps: { label: 'Pastwiska trwałe', category: 'Grunty rolne' },
    Br: { label: 'Grunty rolne zabudowane', category: 'Grunty rolne' },
    Wsr: { label: 'Grunty pod stawami', category: 'Grunty rolne' },
    W: { label: 'Grunty pod rowami', category: 'Grunty rolne' },
    Lzr: { label: 'Grunty zadrzewione i zakrzewione na użytkach rolnych', category: 'Grunty rolne' },
    Ls: { label: 'Lasy', category: 'Grunty leśne oraz zadrzewione' },
    Lz: { label: 'Grunty zadrzewione i zakrzewione', category: 'Grunty leśne oraz zadrzewione' },
    B: { label: 'Tereny mieszkaniowe', category: 'Grunty zabudowane i zurbanizowane' },
    Ba: { label: 'Tereny przemysłowe', category: 'Grunty zabudowane i zurbanizowane' },
    Bi: { label: 'Inne tereny zabudowane', category: 'Grunty zabudowane i zurbanizowane' },
    Bp: { label: 'Zurbanizowane tereny niezabudowane lub w trakcie zabudowy', category: 'Grunty zabudowane i zurbanizowane' },
    Bz: { label: 'Tereny rekreacyjno-wypoczynkowe', category: 'Grunty zabudowane i zurbanizowane' },
    K: { label: 'Użytki kopalne', category: 'Grunty zabudowane i zurbanizowane' },
    dr: { label: 'Drogi', category: 'Tereny komunikacyjne' },
    Tk: { label: 'Tereny kolejowe', category: 'Tereny komunikacyjne' },
    Ti: { label: 'Inne tereny komunikacyjne', category: 'Tereny komunikacyjne' },
    Tp: { label: 'Grunty przeznaczone pod budowę dróg publicznych lub linii kolejowych', category: 'Tereny komunikacyjne' },
    Wm: { label: 'Grunty pod morskimi wodami wewnętrznymi', category: 'Grunty pod wodami' },
    Wp: { label: 'Grunty pod wodami powierzchniowymi płynącymi', category: 'Grunty pod wodami' },
    Ws: { label: 'Grunty pod wodami powierzchniowymi stojącymi', category: 'Grunty pod wodami' },
    N: { label: 'Nieużytki', category: 'Pozostałe użytki' },
    Tr: { label: 'Tereny różne', category: 'Pozostałe użytki' },
};

const LAND_USE_SYMBOLS = ['Lzr', 'Wsr', 'Br', 'Ps', 'Ls', 'Lz', 'Ba', 'Bi', 'Bp', 'Bz', 'Tk', 'Ti', 'Tp', 'Wm', 'Wp', 'Ws', 'Tr', 'dr', 'S', 'Ł', 'R', 'W', 'B', 'K', 'N'];

function landUseSymbol(code) {
    return LAND_USE_SYMBOLS.find(function(symbol) {
        return code === symbol || code.indexOf(symbol + '-') === 0 || code.indexOf(symbol) === 0;
    }) || null;
}

export function landUseCodeDetails(rawCodes) {
    return String(rawCodes == null ? '' : rawCodes).split(',').map(function(rawCode) {
        var code = rawCode.trim();
        if (!code) return null;
        var symbol = landUseSymbol(code);
        var definition = symbol ? LAND_USE_CODES[symbol] : null;
        return {
            code: code,
            symbol: symbol,
            label: definition ? definition.label : 'Nieznany kod użytku gruntowego',
            category: definition ? definition.category : 'Pozostałe użytki',
        };
    }).filter(Boolean);
}

// Only „Ls” (lasy) is forest land in the act on the protection of agricultural and forest land; „Lz”
// trees and shrubs need not be a forest. Same text as DzialkaSzczegolyRenderer::landUseHelp().
export const FOREST_LAND_USE_WARNING = 'budowa domu w lesie wymaga przeznaczenia gruntu na inny cel w planie miejscowym, na co gmina potrzebuje zgody, oraz decyzji o wyłączeniu go z produkcji leśnej, bez względu na klasę gleby. Pod dom jednorodzinny wyłączenie do 500 metrów kwadratowych jest bez opłat; za większą powierzchnię płaci się należność i opłaty roczne.';

export function hasForestLandUse(details) {
    return details.some(function(item) { return item.symbol === 'Ls'; });
}

export function landUseReadable(rawCodes) {
    var details = landUseCodeDetails(rawCodes);
    return details.length
        ? details.map(function(item) { return item.label; }).join(', ')
        : 'Brak danych';
}

/**
 * Help popup of the land use of a parcel in the parcel panel: what the cadastre's
 * land use says to a buyer and where to confirm it.
 */
export function landUseDescription(rawCodes) {
    var details = landUseCodeDetails(rawCodes);
    if (!details.length) {
        return 'Ewidencja gruntów i budynków nie podaje użytku tej działki'
            + '\nZnaczenie: rodzaj użytku i klasę gleby podaje wypis z rejestru gruntów, który wydaje starostwo.'
            + '\n' + guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów');
    }
    var agricultural = details.some(function(item) { return item.category === LAND_USE_CATEGORIES.agricultural.label; });
    var forest = hasForestLandUse(details);
    return 'Sposób użytkowania gruntu zapisany w ewidencji gruntów i budynków'
        + (details.length > 1
            ? '\nUżytki na działce: ' + details.map(function(item) { return item.label + ' — ' + item.category; }).join('; ')
                + '. Bez powierzchni poszczególnych części nie wskazujemy użytku dominującego.'
            : '')
        + '\nZnaczenie: użytek mówi, jak grunt jest używany według ewidencji, i sam nie przesądza o zabudowie. Co wolno zbudować, ustala plan miejscowy albo decyzja o warunkach zabudowy.'
        + (agricultural
            ? '\nUwaga: budowa na gruncie rolnym może wymagać wyłączenia go z produkcji rolniczej. Przy glebach lepszych klas potrzebna jest decyzja starosty i mogą dojść opłaty; klasę gleby podaje wypis z rejestru gruntów.'
            : '')
        + (forest ? '\nUwaga: ' + FOREST_LAND_USE_WARNING : '')
        + '\n' + (agricultural
            ? guideLine('slownik#wylaczenie-gruntu-z-produkcji', 'wyłączenie gruntu z produkcji rolniczej')
            : (forest ? guideLine('slownik#plan-miejscowy', 'plan miejscowy') : guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów')));
}

function startsWithAny(value, prefixes) {
    return prefixes.some(function(prefix) { return value.indexOf(prefix) === 0; });
}

export function landUseCategory(rawCodes) {
    var value = String(rawCodes == null ? '' : rawCodes).trim();
    if (!value) return Object.assign({ key: 'noData' }, LAND_USE_CATEGORIES.noData);
    if (value.indexOf(',') !== -1) return Object.assign({ key: 'mixed' }, LAND_USE_CATEGORIES.mixed);
    if (startsWithAny(value, ['Lzr', 'Wsr'])) return Object.assign({ key: 'agricultural' }, LAND_USE_CATEGORIES.agricultural);
    if (startsWithAny(value, ['Ls', 'Lz'])) return Object.assign({ key: 'forest' }, LAND_USE_CATEGORIES.forest);
    if (value === 'B' || startsWithAny(value, ['Ba', 'Bi', 'Bp', 'Bz']) || value === 'K') {
        return Object.assign({ key: 'builtUp' }, LAND_USE_CATEGORIES.builtUp);
    }
    if (value === 'dr' || ['Tk', 'Ti', 'Tp'].indexOf(value) !== -1) {
        return Object.assign({ key: 'transport' }, LAND_USE_CATEGORIES.transport);
    }
    if (['Wm', 'Wp', 'Ws'].indexOf(value) !== -1) {
        return Object.assign({ key: 'water' }, LAND_USE_CATEGORIES.water);
    }
    if (startsWithAny(value, ['R', 'S-', 'Ł', 'Ps', 'Br']) || value === 'W') {
        return Object.assign({ key: 'agricultural' }, LAND_USE_CATEGORIES.agricultural);
    }
    return Object.assign({ key: 'other' }, LAND_USE_CATEGORIES.other);
}

const RAW_CODES = ['to-string', ['coalesce', ['get', 'klasouzytki_egib'], '']];
const PREFIX = function(length) { return ['slice', ['var', 'codes'], 0, length]; };

export const LAND_USE_COLOR_EXPRESSION = [
    'let', 'codes', RAW_CODES,
    [
        'case',
        ['==', ['var', 'codes'], ''], LAND_USE_CATEGORIES.noData.color,
        ['>=', ['index-of', ',', ['var', 'codes']], 0], LAND_USE_CATEGORIES.mixed.color,
        ['in', PREFIX(3), ['literal', ['Lzr', 'Wsr']]], LAND_USE_CATEGORIES.agricultural.color,
        ['in', PREFIX(2), ['literal', ['Ls', 'Lz']]], LAND_USE_CATEGORIES.forest.color,
        ['any',
            ['in', ['var', 'codes'], ['literal', ['B', 'K']]],
            ['in', PREFIX(2), ['literal', ['Ba', 'Bi', 'Bp', 'Bz']]]
        ], LAND_USE_CATEGORIES.builtUp.color,
        ['in', ['var', 'codes'], ['literal', ['dr', 'Tk', 'Ti', 'Tp']]], LAND_USE_CATEGORIES.transport.color,
        ['in', ['var', 'codes'], ['literal', ['Wm', 'Wp', 'Ws']]], LAND_USE_CATEGORIES.water.color,
        ['any',
            ['in', PREFIX(1), ['literal', ['R', 'Ł', 'W']]],
            ['in', PREFIX(2), ['literal', ['S-', 'Ps', 'Br']]]
        ], LAND_USE_CATEGORIES.agricultural.color,
        LAND_USE_CATEGORIES.other.color,
    ],
];
